import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdAccountCircle, MdArrowBack, MdAttachMoney, MdBusinessCenter, MdCheckCircle, MdChevronRight, MdCompareArrows, MdErrorOutline, MdEventBusy, MdGroups, MdHealthAndSafety, MdHistoryToggleOff, MdHub, MdLeaderboard, MdLocationOn, MdMedicalServices, MdMoneyOff, MdPeople, MdPerson, MdPersonOff, MdReceiptLong, MdSchedule, MdSearch, MdTrendingDown, MdTrendingUp, MdVisibility, MdWarningAmber, MdWorkspaces } from 'react-icons/md';
import BaseChart from '../../Components/Analytics/BaseChart';
import DataGrid from '../../Components/Table/DataGrid';
import Modal from '../../Components/Common/Modal';
import { feeBarOption, feeHistogramOption, feeRangeOption, feeShareDonutOption } from '../../Utils/AnalyticsOptions';
import { US_STATE_NAMES, transformFeeNameValues } from '../../Utils/AnalyticsTransformers';
import { getFeeScheduleDataQuality, getFeeScheduleExpiringRates, getFeeScheduleGroupRates, getFeeScheduleMyRateCard, getFeeScheduleNetworkPlans, getFeeScheduleOverview, getFeeScheduleProviderRates, getFeeScheduleSpecialties, getFeeScheduleSpecialtyRates, getFeeScheduleZipRates } from '../../Services/FeeScheduleAnalyticsService';
import { getAllProviders } from '../../Services/ProviderService';
import { SectionBar } from '../Dashboard/Dashboard';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { toast } from 'react-toastify';
import { theme } from '../../Utils/theme';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { useChartTheme } from '../../Context/ThemeContext';
import PageTitle from '../../Components/Common/PageTitle';
import AnalyticsChatWidget from '../../Components/Analytics/AnalyticsChatWidget';
import { endpoints } from '../../Utils/Constants';
import { PermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const count = (v) => num(v).toLocaleString();

const usd = (v) => (v == null ? '—' : `$${num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const titleCase = (v) => (v ? String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

const MODIFIER_OPTIONS = [
  { value: 'none', label: 'Global (no modifier)' },
  { value: '26', label: '26 — Professional' },
  { value: 'TC', label: 'TC — Technical' },
];

const SETTING_OPTIONS = [
  { value: 'all', label: 'All settings' },
  { value: 'office', label: 'Office (non-facility)' },
  { value: 'facility', label: 'Facility' },
];

/**
 * Runs a ResponseModel request and tracks { data, loading, error }; a newer run or unmount aborts the previous one.
 * `request(signal)` must resolve to the parsed JSON body.
 */
function useApiRequest() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);

  const run = useCallback(async (request) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const res = await request(controller.signal);
      if (!res?.is_success) throw new Error(res?.message || 'Request failed');
      setData(res.data);
      return res.data;
    } catch (err) {
      if (err.name === 'AbortError') return null;
      setData(null);
      setError(err);
      return null;
    } finally {
      if (controllerRef.current === controller) setLoading(false);
    }
  }, []);

  useEffect(() => () => controllerRef.current?.abort(), []);

  return { data, loading, error, run };
}

/** KPI card: tinted icon badge beside the label, value on its own full-width line so numbers never wrap.
 *  `loading` swaps the value for a skeleton bar. */
function MetricCard({ icon, label, value, hint, loading = false }) {
  return (
    <div className="hc-surface rounded-4 p-3 h-100 d-flex flex-column" style={{ minWidth: 0 }}>
      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
        <div className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 40, height: 40, backgroundColor: 'rgba(60, 159, 162, 0.14)', color: 'var(--hc-brand-ink)' }}>
          {icon}
        </div>
        <div className="text-secondary small text-uppercase fw-semibold lh-sm" style={{ minWidth: 0 }}>{label}</div>
      </div>
      {loading ? (
        <div className="skeleton position-relative overflow-hidden rounded-2 mt-3" style={{ height: 26, width: 90 }} />
      ) : (
        <div className="fw-bold mt-3 text-nowrap text-truncate" style={{ fontSize: 'clamp(1.15rem, 1vw + 0.8rem, 1.5rem)' }} title={typeof value === 'string' ? value : undefined}>
          {value}
        </div>
      )}
      {hint ? <div className="text-secondary small mt-1">{hint}</div> : null}
    </div>
  );
}

/** Translucent KPI tile for the hc-hero-banner; icon sits on a white badge. */
function HeroStatTile({ icon, label, value }) {
  return (
    <div className="d-flex align-items-center gap-3 p-3 rounded-4 h-100" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
      <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
        {icon}
      </div>
      <div className="text-truncate">
        <p className="mb-1 small text-white">{label}</p>
        <p className="fw-bold mb-0 text-white">{value}</p>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  completed: { bg: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  failed: { bg: 'var(--hc-status-danger-bg)', color: 'var(--hc-status-danger-text)' },
};
const DEFAULT_STATUS_STYLE = { bg: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)' };

const PLAN_COLUMNS = [
  { id: 'plan_sponsor_name', label: 'Employer / Sponsor', minWidth: 260, filterable: true, format: (value) => value || '—' },
  { id: 'plan_name', label: 'Plan', minWidth: 120, filterable: true, format: (value) => value || '—' },
  { id: 'plan_market_type', label: 'Market', minWidth: 110, align: 'center', format: (value) => titleCase(value) },
  { id: 'plan_id', label: 'Plan ID', minWidth: 130, align: 'center', format: (value) => value || '—' },
];

function FeeNetworksTab({ overview, loading, token, insuranceId }) {
  const plans = useApiRequest();
  const [planNetwork, setPlanNetwork] = React.useState(null);
  const summary = overview?.summary || {};
  const networks = React.useMemo(() => overview?.networks || [], [overview]);

  const { run: runPlans } = plans;
  const openPlans = React.useCallback((network) => {
    setPlanNetwork(network);
    runPlans((signal) => getFeeScheduleNetworkPlans(token, insuranceId, network.source_file_id, signal));
  }, [runPlans, token, insuranceId]);

  const largest = React.useMemo(
    () => networks.slice(0, 15).map((n) => ({ name: n.network_name, value: n.rate_count })),
    [networks],
  );
  const statusData = React.useMemo(
    () => transformFeeNameValues((overview?.status_counts || []).map((s) => ({ name: titleCase(s.name), value: s.value }))),
    [overview],
  );

  const columns = React.useMemo(() => [
    { id: 'network_name', label: 'Network', minWidth: 240, filterable: true },
    { id: 'reporting_month', label: 'Reporting Month', minWidth: 140, align: 'center', format: (value) => value || '—' },
    {
      id: 'status',
      label: 'Ingestion',
      minWidth: 150,
      align: 'center',
      format: (value, row) => {
        const style = STATUS_STYLE[value] || DEFAULT_STATUS_STYLE;
        return (
          <span
            className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-semibold"
            style={{ backgroundColor: style.bg, color: style.color, fontSize: '0.75rem' }}
            title={row.error || ''}
          >
            {value === 'completed' ? <MdCheckCircle size={14} /> : <MdErrorOutline size={14} />}
            {titleCase(value)}
          </span>
        );
      },
    },
    { id: 'rate_count', label: 'Rates', minWidth: 110, align: 'center', format: (value) => count(value) },
    { id: 'provider_count', label: 'Provider Refs', minWidth: 130, align: 'center', format: (value) => count(value) },
    { id: 'duplicate_rate_count', label: 'Duplicates', minWidth: 110, align: 'center', format: (value) => count(value) },
    {
      id: 'actions',
      label: 'Employer Plans',
      minWidth: 150,
      align: 'center',
      format: (_value, row) => (
        <button
          type="button"
          className="btn btn-sm px-2 py-1 border-0 rounded-2 text-primary fw-semibold"
          style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
          onClick={() => openPlans(row)}
        >
          View employers
        </button>
      ),
    },
  ], [openPlans]);

  const cards = [
    { label: 'Networks', value: count(summary.network_count), icon: <MdHub size={22} /> },
    { label: 'Completed', value: count(summary.completed_network_count), icon: <MdCheckCircle size={22} /> },
    { label: 'Failed', value: count(summary.failed_network_count), icon: <MdErrorOutline size={22} /> },
    { label: 'Rates in Files', value: count(summary.rate_count), icon: <MdReceiptLong size={22} /> },
    { label: 'Provider References', value: count(summary.provider_reference_count), icon: <MdBusinessCenter size={22} /> },
    { label: 'Billing Codes', value: count(summary.code_count), icon: <MdWorkspaces size={22} /> },
  ];

  return (
    <div className="row g-4">
      {cards.map((card) => (
        <div key={card.label} className="col-12 col-sm-6 col-lg-4 col-xxl-2">
          <MetricCard {...card} loading={loading} />
        </div>
      ))}

      <div className="col-12 col-xl-4">
        <BaseChart
          title="Ingestion status"
          subtitle="Network files by pipeline status"
          loading={loading}
          noData={!loading && !statusData.length}
          option={feeShareDonutOption({ data: statusData, title: 'Ingestion Status', subtitle: 'Network files by pipeline status' })}
        />
      </div>
      <div className="col-12 col-xl-8">
        <BaseChart
          title="Largest networks"
          subtitle="Negotiated rates per network file"
          loading={loading}
          noData={!loading && !largest.length}
          option={feeBarOption({ data: largest, title: 'Largest Networks', subtitle: 'Negotiated rates per network file' })}
        />
      </div>

      <div className="col-12">
        <div className="hc-surface rounded-4 p-3 shadow-sm">
          <h6 className="fw-bold hc-text-primary mb-3">All networks</h6>
          <DataGrid
            columns={columns}
            rows={networks}
            loading={loading}
            skeletonRows={6}
            defaultRowsPerPage={10}
            stickyHeader
            getRowId={(row) => row.source_file_id}
          />
        </div>
      </div>

      <Modal
        open={Boolean(planNetwork)}
        onClose={() => setPlanNetwork(null)}
        title={planNetwork?.network_name || 'Employer plans'}
        subtitle="Employers and plans that use this network"
        icon={<MdBusinessCenter size={20} />}
        size="xl"
        scrollable
      >
        {plans.error ? <div className="alert alert-danger mb-3">{plans.error.message}</div> : null}
        <div className="row g-3">
          <div className="col-12 col-lg-4">
            <BaseChart
              className="h-100"
              title="Market type"
              subtitle="Plans by market"
              height="300px"
              loading={plans.loading}
              noData={!plans.loading && !(plans.data?.market_types || []).length}
              option={feeShareDonutOption({ data: transformFeeNameValues(plans.data?.market_types), title: 'Market Type', subtitle: 'Plans by market' })}
            />
          </div>
          <div className="col-12 col-lg-8 hc-fill-grid">
            <DataGrid
              columns={PLAN_COLUMNS}
              rows={plans.data?.plans || []}
              loading={plans.loading}
              skeletonRows={10}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => `${row.plan_sponsor_name}-${row.plan_id}-${row.plan_name}`}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** Highest/lowest paid providers, the rate spread and details for one area (a zip or a specialty in a state). */
function AreaRankingResults({ data, loading, error, scope, area, highlightRequested = false, onOpenProvider, specialty = null }) {
  const top = React.useMemo(() => (data?.top_paid || []).map((p) => ({ name: p.name, value: p.median })), [data]);
  const bottom = React.useMemo(() => (data?.lowest_paid || []).map((p) => ({ name: p.name, value: p.median })), [data]);
  const highlight = data?.highlight;
  // Paid-more list arrives highest first, so the highlighted NPI sits between its nearest higher and lower peers.
  const around = React.useMemo(() => (highlight ? [
    ...(data.highlight_paid_more || []).map((p) => ({ name: p.name, value: p.median })),
    { name: highlight.name, value: highlight.median, color: theme.gradientEnd() },
    ...(data.highlight_paid_less || []).map((p) => ({ name: p.name, value: p.median })),
  ] : []), [data, highlight]);
  // Small areas make the highest, lowest and around-the-highlight lists overlap; each provider is listed once, first list wins.
  // Small areas make the highest, lowest and around-the-highlight lists overlap; each provider is listed once, first list wins.
  const tableRows = React.useMemo(() => {
    // The zip search returns every provider, so the table lists them all by rank and column filters see the whole zip.
    if (data?.providers) {
      const selected = data.highlight;
      return data.providers.map((p) => {
        if (p.npi === selected?.npi) return { ...p, group: 'Selected' };
        if (!selected) return { ...p, group: 'Rank' };
        if (p.median > selected.median) return { ...p, group: 'Paid more' };
        return { ...p, group: p.median < selected.median ? 'Paid less' : 'Same rate' };
      });
    }
    const seen = new Set();
    return [
      ...(data?.highlight ? [{ ...data.highlight, group: 'Selected' }] : []),
      ...(data?.top_paid || []).map((p, idx) => ({ ...p, group: 'Highest', rank: idx + 1 })),
      ...(data?.lowest_paid || []).map((p, idx) => ({ ...p, group: 'Lowest', rank: idx + 1 })),
      ...(data?.highlight_paid_more || []).slice().reverse().map((p, idx) => ({ ...p, group: 'Paid more', rank: idx + 1 })),
      ...(data?.highlight_paid_less || []).map((p, idx) => ({ ...p, group: 'Paid less', rank: idx + 1 })),
    ].filter((p) => (seen.has(p.npi) ? false : seen.add(p.npi)));
  }, [data]);

  const stats = data?.stats || {};
  const hasResult = Boolean(data) && data.provider_count > 0;
  // The API never repeats top providers in the lowest list, so an area of five or fewer has a single ranking.
  const showLowest = !hasResult || bottom.length > 0;
  const topTitle = hasResult && !bottom.length ? `All ${data.provider_count} providers ranked` : `Top ${hasResult ? top.length : 5} highest paid`;
  const lowestTitle = `Top ${hasResult ? bottom.length : 5} lowest paid`;
  // Specialty compares a handful of named providers, so the extremes read better than percentiles.
  const spreadCards = scope === 'Specialty'
    ? [
      { label: 'Lowest Paid', value: usd(stats.min), icon: <MdTrendingDown size={22} /> },
      { label: 'Highest Paid', value: usd(stats.max), icon: <MdTrendingUp size={22} /> },
    ]
    : [
      { label: '10th Percentile', value: usd(stats.p10), icon: <MdTrendingDown size={22} /> },
      { label: '90th Percentile', value: usd(stats.p90), icon: <MdTrendingUp size={22} /> },
    ];
  const cards = [
    { label: `Providers in ${scope}`, value: count(data?.provider_count), icon: <MdPeople size={22} /> },
    { label: `${scope} Median`, value: usd(stats.median), icon: <MdAttachMoney size={22} /> },
    ...spreadCards,
  ];
  // The previous result's highlight is stale while a new search loads.
  const showHighlight = !loading && Boolean(highlight);
  const highlightLoading = loading && highlightRequested;

  const columns = React.useMemo(() => [
    {
      id: 'group',
      label: data?.providers ? 'Rank' : 'Group',
      minWidth: 120,
      format: (value, row) => {
        const tone = { Selected: 'hc-text-primary', Highest: 'text-success', 'Paid more': 'text-success', Lowest: 'text-danger', 'Paid less': 'text-danger' }[value] || 'text-secondary';
        // With the full list, the number is the provider's rank in the area rather than a position within a group.
        const text = value === 'Rank' ? `#${row.rank}` : data?.providers ? `#${row.rank} · ${value}` : `${value} #${row.rank}`;
        return <span className={`fw-semibold text-nowrap ${tone}`}>{text}</span>;
      },
    },
    {
      id: 'name',
      label: 'Provider',
      minWidth: 220,
      format: (value, row) => (row.npi === highlight?.npi ? (
        <span className="d-inline-flex align-items-center gap-2">
          {value}
          <span className="badge rounded-pill" style={{ backgroundColor: 'var(--hc-gradient-end)', color: 'var(--hc-gradient-start)' }}>Selected</span>
        </span>
      ) : value),
    },
    { id: 'npi', label: 'NPI', minWidth: 120, align: 'center' },
    { id: 'specialty', label: 'Specialty', minWidth: 200, format: (value) => value || '—' },
    { id: 'entity_type', label: 'Type', minWidth: 110, align: 'center' },
    { id: 'median', label: 'Median Rate', minWidth: 120, align: 'center', format: (value) => <strong>{usd(value)}</strong> },
    { id: 'min', label: 'Range', minWidth: 170, align: 'center', format: (value, row) => (row.min === row.max ? usd(value) : `${usd(row.min)} – ${usd(row.max)}`) },
    { id: 'network_count', label: 'Networks', minWidth: 100, align: 'center' },
    {
      id: 'actions',
      label: 'Rate Card',
      minWidth: 110,
      align: 'center',
      format: (_value, row) => (
        <button
          type="button"
          title="Open rate card"
          className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
          style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
          onClick={() => onOpenProvider(row.npi)}
        >
          <MdVisibility size={16} />
        </button>
      ),
    },
  ], [onOpenProvider, highlight, data?.providers]);

  return (
    <>
      {cards.map((card) => (
        <div key={card.label} className="col-12 col-sm-6 col-xl-3">
          <MetricCard {...card} loading={loading} />
        </div>
      ))}
      {highlightLoading ? (
        <div className="col-12">
          <div className="hc-surface rounded-4 p-3 d-flex align-items-center gap-3 border-start border-5 border-warning">
            <MdLeaderboard size={24} className="hc-text-primary flex-shrink-0" />
            <div className="flex-grow-1">
              <div className="skeleton position-relative overflow-hidden rounded-2 mb-2" style={{ height: 18, width: '30%' }} />
              <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 14, width: '60%' }} />
            </div>
          </div>
        </div>
      ) : null}
      {showHighlight && specialty ? (
        <SideBySideRankings
          panels={[
            { data, area, label: 'All providers', icon: <MdLeaderboard size={24} className="hc-text-primary flex-shrink-0" /> },
            { data: specialty, area, label: specialty.specialty, icon: <MdMedicalServices size={24} className="hc-text-primary flex-shrink-0" /> },
          ]}
        />
      ) : (
        <>
          {showHighlight ? (
            <div className="col-12">
              <div className="hc-surface rounded-4 p-3 d-flex flex-wrap align-items-center gap-3 border-start border-5 border-warning">
                <MdLeaderboard size={24} className="hc-text-primary" />
                <div>
                  <div className="fw-bold">{highlight.name} · {usd(highlight.median)}</div>
                  <div className="text-secondary small">
                    Ranked #{highlight.rank} of {count(data.provider_count)} providers in {area} for {data.code}:{' '}
                    {count(highlight.paid_more_count)} paid more, {count(highlight.paid_less_count)} paid less
                    {highlight.same_rate_count ? `, ${count(highlight.same_rate_count)} paid the same rate` : ''}.
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {showHighlight || highlightLoading ? (
            <div className="col-12">
              <BaseChart
                title={showHighlight ? `Providers ranked around ${highlight.name}` : 'Providers ranked around the selected NPI'}
                subtitle="Nearest 5 paid more and 5 paid less; highlighted provider in amber"
                height="420px"
                loading={loading}
                error={error}
                noData={!loading && around.length < 2}
                option={feeBarOption({ data: around, title: `Providers Ranked Around ${highlight?.name || ''}`, subtitle: `Median rate for ${data?.code || ''} in ${area || ''}`, currency: true })}
              />
            </div>
          ) : null}
          {data?.provider_count === 0 ? (
            <div className="col-12">
              <div className="alert alert-warning mb-0">No providers in {area} have a {data.code} rate from this payer with these filters.</div>
            </div>
          ) : null}

          <div className={showLowest ? 'col-12 col-xl-6' : 'col-12'}>
            <BaseChart
              title={topTitle}
              subtitle="Provider median rate"
              height="360px"
              loading={loading}
              error={error}
              noData={!loading && !hasResult}
              option={feeBarOption({ data: top, title: topTitle, subtitle: `Median rate for ${data?.code || ''} in ${area || ''}`, currency: true })}
            />
          </div>
          {showLowest ? (
            <div className="col-12 col-xl-6">
              <BaseChart
                title={lowestTitle}
                subtitle="Provider median rate"
                height="360px"
                loading={loading}
                error={error}
                noData={!loading && !hasResult}
                option={feeBarOption({ data: bottom, title: lowestTitle, subtitle: `Median rate for ${data?.code || ''} in ${area || ''}`, currency: true, color: theme.gradientEnd() })}
              />
            </div>
          ) : null}
        </>
      )}
      <div className="col-12">
        <BaseChart
          title={`Rate distribution in ${scope.toLowerCase()}`}
          subtitle="Providers per rate band"
          loading={loading}
          error={error}
          noData={!loading && !hasResult}
          option={feeHistogramOption({ bins: data?.histogram || [], median: stats.median, highlight })}
        />
      </div>
      <div className="col-12">
        <div className="hc-surface rounded-4 p-3 shadow-sm">
          <h6 className="fw-bold hc-text-primary mb-3">Provider details</h6>
          <DataGrid columns={columns} rows={tableRows} loading={loading} skeletonRows={5} defaultRowsPerPage={10} getRowId={(row) => `${row.group}-${row.npi}`} />
        </div>
      </div>
    </>
  );
}

/** One area's banner, around-the-highlight, highest and lowest charts, laid out as rows of two so the zip and the specialty sit side by side. */
function SideBySideRankings({ panels }) {
  const toBars = (rows) => (rows || []).map((p) => ({ name: p.name, value: p.median }));
  const built = panels.map(({ data, area, label, icon }) => {
    const highlight = data.highlight;
    const top = toBars(data.top_paid);
    const bottom = toBars(data.lowest_paid);
    const subtitle = `Median rate for ${data.code} among ${label} in ${area}`;
    return {
      key: label,
      banner: (
        <div className="hc-surface rounded-4 p-3 h-100 d-flex align-items-center gap-3 border-start border-5 border-warning">
          {icon}
          <div>
            <div className="text-uppercase small fw-semibold text-secondary">{label} in {area}</div>
            <div className="fw-bold">{highlight.name} · {usd(highlight.median)}</div>
            <div className="text-secondary small">
              Ranked #{highlight.rank} of {count(data.provider_count)} for {data.code}:{' '}
              {count(highlight.paid_more_count)} paid more, {count(highlight.paid_less_count)} paid less
              {highlight.same_rate_count ? `, ${count(highlight.same_rate_count)} paid the same rate` : ''}.
            </div>
          </div>
        </div>
      ),
      around: (
        <BaseChart
          title={`Ranked around ${highlight.name}`}
          subtitle={`${label}: nearest 5 paid more and 5 paid less`}
          height="420px"
          noData={false}
          option={feeBarOption({
            data: [
              ...toBars(data.highlight_paid_more),
              { name: highlight.name, value: highlight.median, color: theme.gradientEnd() },
              ...toBars(data.highlight_paid_less),
            ],
            title: `${label} Ranked Around ${highlight.name}`,
            subtitle,
            currency: true,
          })}
        />
      ),
      top: (
        <BaseChart
          title={bottom.length ? `Top ${top.length} highest paid` : `All ${data.provider_count} ranked`}
          subtitle={label}
          height="360px"
          noData={!top.length}
          option={feeBarOption({ data: top, title: `${label}: highest paid`, subtitle, currency: true })}
        />
      ),
      // The API never repeats top providers in the lowest list, so an area of five or fewer has no separate lowest list.
      bottom: bottom.length ? (
        <BaseChart
          title={`Top ${bottom.length} lowest paid`}
          subtitle={label}
          height="360px"
          option={feeBarOption({ data: bottom, title: `${label}: lowest paid`, subtitle, currency: true, color: theme.gradientEnd() })}
        />
      ) : (
        <div className="hc-surface rounded-4 p-4 h-100 d-flex align-items-center justify-content-center text-center text-secondary">
          Only {count(data.provider_count)} {label.toLowerCase()} in {area}; all are shown in the highest paid chart.
        </div>
      ),
    };
  });

  return ['banner', 'around', 'top', 'bottom'].map((row) => built.map((panel) => (
    <div key={`${row}-${panel.key}`} className="col-12 col-xl-6">{panel[row]}</div>
  )));
}

const ZIP_PATTERN = /^\d{5}$/;
const NPI_PATTERN = /^\d{10}$/;
const EMPTY_FORM = { cptCode: '99213', zip: '', modifier: 'none', setting: 'all', npi: '' };

function FeeZipTab({ codes, token, insuranceId, prefill, onOpenProvider }) {
  const { data, loading, error, run } = useApiRequest();
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [highlightRequested, setHighlightRequested] = React.useState(false);

  const search = React.useCallback((values) => {
    if (!values.cptCode) return toast.error('Select a billing code.');
    if (!ZIP_PATTERN.test(values.zip)) return toast.error('Enter a 5-digit zip code.');
    if (values.npi && !NPI_PATTERN.test(values.npi)) return toast.error('NPI must be 10 digits.');
    setHighlightRequested(Boolean(values.npi));
    return run((signal) => getFeeScheduleZipRates(token, insuranceId, values, signal));
  }, [token, insuranceId, run]);

  React.useEffect(() => {
    if (!prefill) return;
    const next = { ...EMPTY_FORM, cptCode: prefill.code, zip: prefill.zip || '', npi: prefill.npi ? String(prefill.npi) : '' };
    setForm(next);
    if (next.zip) search(next);
  }, [prefill, search]);

  const update = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const codeOptions = React.useMemo(() => codes.map((c) => ({ value: c, label: c })), [codes]);

  return (
    <div className="row g-4">
      <div className="col-12">
        <form
          className="hc-surface rounded-4 p-3 shadow-sm row g-3 align-items-end mx-0"
          onSubmit={(event) => { event.preventDefault(); search(form); }}
        >
          <div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
            <label className="form-label small fw-semibold text-secondary mb-1">Billing code</label>
            <SearchableDropdown options={codeOptions} value={form.cptCode} onChange={(v) => v && update('cptCode')(v)} placeholder="Code" />
          </div>
          <div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
            <label className="form-label small fw-semibold text-secondary mb-1" htmlFor="fee-zip">Zip code</label>
            <input id="fee-zip" className="form-control" inputMode="numeric" maxLength={5} placeholder="e.g. 77030" value={form.zip} onChange={(e) => update('zip')(e.target.value.replace(/\D/g, ''))} style={{ height: 40 }} />
          </div>
          <div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
            <label className="form-label small fw-semibold text-secondary mb-1">Modifier</label>
            <SearchableDropdown options={MODIFIER_OPTIONS} value={form.modifier} onChange={(v) => v && update('modifier')(v)} />
          </div>
          <div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
            <label className="form-label small fw-semibold text-secondary mb-1">Setting</label>
            <SearchableDropdown options={SETTING_OPTIONS} value={form.setting} onChange={(v) => v && update('setting')(v)} />
          </div>
          <div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
            <label className="form-label small fw-semibold text-secondary mb-1" htmlFor="fee-zip-npi">Highlight NPI (optional)</label>
            <input id="fee-zip-npi" className="form-control" inputMode="numeric" maxLength={10} placeholder="10-digit NPI" value={form.npi} onChange={(e) => update('npi')(e.target.value.replace(/\D/g, ''))} style={{ height: 40 }} />
          </div>
          <div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
            <button type="submit" className="btn hc-btn-primary-gradient text-white fw-semibold w-100 d-inline-flex align-items-center justify-content-center gap-2" style={{ height: 40 }} disabled={loading}>
              <MdSearch size={18} />
              {loading ? 'Searching…' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {!data && !loading && !error ? (
        <div className="col-12">
          <div className="hc-surface rounded-4 p-4 text-center text-secondary">
            Enter a billing code and a 5-digit zip to see the highest and lowest paid providers for this payer.
          </div>
        </div>
      ) : null}

      {data || loading || error ? (
        <AreaRankingResults data={data} loading={loading} error={error} scope="Zip" area={data?.zip} highlightRequested={highlightRequested} onOpenProvider={onOpenProvider} specialty={data?.specialty_ranking} />
      ) : null}
    </div>
  );
}

const STATE_OPTIONS = Object.entries(US_STATE_NAMES).map(([value, name]) => ({ value, label: `${name} (${value})` }));
const EMPTY_SPECIALTY_FORM = { specialty: '', state: '', cptCode: '99213', modifier: 'none', setting: 'all' };

function FeeSpecialtyTab({ codes, token, insuranceId, onOpenProvider }) {
  const specialties = useApiRequest();
  const { run: runSpecialties } = specialties;
  const { data, loading, error, run } = useApiRequest();
  const [form, setForm] = React.useState(EMPTY_SPECIALTY_FORM);

  React.useEffect(() => {
    runSpecialties((signal) => getFeeScheduleSpecialties(token, signal));
  }, [token, runSpecialties]);

  const search = (values) => {
    if (!values.specialty) return toast.error('Select a specialty.');
    if (!values.state) return toast.error('Select a state.');
    if (!values.cptCode) return toast.error('Select a billing code.');
    return run((signal) => getFeeScheduleSpecialtyRates(token, insuranceId, values, signal));
  };

  const update = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const codeOptions = React.useMemo(() => codes.map((c) => ({ value: c, label: c })), [codes]);
  const specialtyOptions = React.useMemo(() => (specialties.data || []).map((s) => ({ value: s, label: s })), [specialties.data]);

  return (
    <div className="row g-4">
      <div className="col-12">
        <form
          className="hc-surface rounded-4 p-3 shadow-sm row g-3 align-items-end mx-0"
          onSubmit={(event) => { event.preventDefault(); search(form); }}
        >
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold text-secondary mb-1">Specialty</label>
            <SearchableDropdown
              options={specialtyOptions}
              value={form.specialty}
              onChange={(v) => v && update('specialty')(v)}
              placeholder={specialties.loading ? 'Loading specialties…' : 'Select a specialty'}
              emptyMessage="No specialties found"
            />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold text-secondary mb-1">State</label>
            <SearchableDropdown options={STATE_OPTIONS} value={form.state} onChange={(v) => v && update('state')(v)} placeholder="Select a state" emptyMessage="No states found" />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold text-secondary mb-1">Billing code</label>
            <SearchableDropdown options={codeOptions} value={form.cptCode} onChange={(v) => v && update('cptCode')(v)} placeholder="Code" />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold text-secondary mb-1">Modifier</label>
            <SearchableDropdown options={MODIFIER_OPTIONS} value={form.modifier} onChange={(v) => v && update('modifier')(v)} />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold text-secondary mb-1">Setting</label>
            <SearchableDropdown options={SETTING_OPTIONS} value={form.setting} onChange={(v) => v && update('setting')(v)} />
          </div>
          <div className="col-12 col-md-4">
            <button type="submit" className="btn hc-btn-primary-gradient text-white fw-semibold w-100 d-inline-flex align-items-center justify-content-center gap-2" style={{ height: 40 }} disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm" /> : <MdSearch size={18} />}
              {loading ? 'Searching…' : 'Compare'}
            </button>
          </div>
        </form>
      </div>

      {specialties.error ? <div className="col-12"><div className="alert alert-danger mb-0">{specialties.error.message}</div></div> : null}
      {!data && !loading && !error ? (
        <div className="col-12">
          <div className="hc-surface rounded-4 p-4 text-center text-secondary">
            Pick a specialty, a state and a billing code to compare what this payer pays providers of that specialty.
          </div>
        </div>
      ) : null}

      {data || loading || error ? (
        <AreaRankingResults
          data={data}
          loading={loading}
          error={error}
          scope="Specialty"
          area={data ? `${data.specialty} · ${US_STATE_NAMES[data.state] || data.state}` : ''}
          onOpenProvider={onOpenProvider}
        />
      ) : null}
    </div>
  );
}

const MAX_RANGE_CODES = 15;
const MAX_NETWORK_LINES = 5;

const priceRange = (min, max) => (min === max ? usd(min) : `${usd(min)} – ${usd(max)}`);

// Each network's price for a code (or its range when one network pays several prices), best-paying first.
const NETWORK_RATES_COLUMN = {
  id: 'network_rates',
  label: 'Paid by Network',
  minWidth: 340,
  sortAccessor: (row) => row.network_count,
  filterAccessor: (row) => (row.network_rates || []).map((n) => n.network).join(', '),
  format: (value) => {
    const rates = value || [];
    const hidden = rates.slice(MAX_NETWORK_LINES);
    return (
      <div className="d-flex flex-column gap-1 py-1">
        {rates.slice(0, MAX_NETWORK_LINES).map((n) => (
          <div key={n.network} className="d-flex justify-content-between gap-3 small">
            <span className="text-truncate" title={n.network}>{n.network}</span>
            <strong className="text-nowrap">{priceRange(n.min, n.max)}</strong>
          </div>
        ))}
        {hidden.length ? (
          <span className="small text-secondary" title={hidden.map((n) => `${n.network}: ${priceRange(n.min, n.max)}`).join('\n')}>
            +{hidden.length} more network{hidden.length > 1 ? 's' : ''}
          </span>
        ) : null}
      </div>
    );
  },
};

/** Provider card, billing TINs, price-difference chart and the filterable rate card for one NPI. */
function ProviderRateCardResults({ data, loading, onOpenZip, onOpenGroup }) {
  const [codeSearch, setCodeSearch] = React.useState('');
  const provider = data?.provider;
  const tins = data?.tins || [];
  const zip = provider?.zip || '';

  const varying = React.useMemo(
    () => (data?.codes || [])
      .filter((row) => row.price_count > 1)
      .sort((a, b) => (b.max - b.min) - (a.max - a.min))
      .slice(0, MAX_RANGE_CODES)
      .map((row) => ({ name: row.modifier === 'None' ? row.code : `${row.code}-${row.modifier}`, min: row.min, median: row.median, max: row.max, networks: row.network_rates })),
    [data],
  );
  const codes = React.useMemo(() => {
    const term = codeSearch.trim().toLowerCase();
    const rows = data?.codes || [];
    return term ? rows.filter((row) => row.code.toLowerCase().includes(term)) : rows;
  }, [data, codeSearch]);

  const columns = React.useMemo(() => [
    { id: 'code', label: 'Code', minWidth: 90 },
    { id: 'modifier', label: 'Modifier', minWidth: 100, align: 'center' },
    { id: 'median', label: 'Median', minWidth: 110, align: 'center', format: (value) => <strong>{usd(value)}</strong> },
    { id: 'min', label: 'Lowest', minWidth: 110, align: 'center', format: (value) => usd(value) },
    { id: 'max', label: 'Highest', minWidth: 110, align: 'center', format: (value) => usd(value) },
    NETWORK_RATES_COLUMN,
    {
      id: 'actions',
      label: 'Compare in Zip',
      minWidth: 140,
      align: 'center',
      format: (_value, row) => (
        <button
          type="button"
          title={zip ? `Compare with providers in ${zip}` : 'No zip on file'}
          disabled={!zip}
          className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
          style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
          onClick={() => onOpenZip({ code: row.code, zip, npi: data.npi })}
        >
          <MdCompareArrows size={16} />
        </button>
      ),
    },
  ], [zip, onOpenZip, data]);

  return (
    <>
      {provider ? (
        <div className="col-12">
          <div className="hc-surface rounded-4 p-3 d-flex align-items-start gap-3">
            <div className="d-inline-flex align-items-center justify-content-center rounded-4 flex-shrink-0" style={{ width: 48, height: 48, backgroundColor: 'rgba(60, 159, 162, 0.14)' }}>
              <MdPerson size={24} className="hc-text-primary" />
            </div>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="fw-bold">{provider.name}</div>
              <div className="text-secondary small">{provider.specialty || 'Specialty unknown'} · {provider.entity_type}</div>
              <div className="text-secondary small">{[provider.city, provider.state, provider.zip].filter(Boolean).join(', ') || 'Address unknown'}</div>
              {provider.npi_lookup_status === 'not_found' ? <div className="small text-danger mt-1">NPI not found in the national registry</div> : null}
              <div className="small fw-semibold text-secondary mt-3 mb-2">Billing TINs with this payer ({count(tins.length)}) · click a TIN to open its group rates</div>
              <div className="d-flex flex-wrap gap-2">
                {tins.map((t) => (
                  <button
                    key={`${t.tin_type}-${t.tin}`}
                    type="button"
                    title="View every provider billing under this TIN"
                    className="btn btn-sm rounded-pill fw-semibold d-inline-flex align-items-center gap-1 px-3 py-1 shadow-sm"
                    style={{
                      fontSize: '0.85rem',
                      '--bs-btn-color': 'var(--hc-accent-teal)',
                      '--bs-btn-bg': 'rgba(var(--hc-gradient-mid-rgb), 0.08)',
                      '--bs-btn-border-color': 'var(--hc-accent-teal)',
                      '--bs-btn-hover-color': '#fff',
                      '--bs-btn-hover-bg': 'var(--hc-accent-teal)',
                      '--bs-btn-hover-border-color': 'var(--hc-accent-teal)',
                      '--bs-btn-active-color': '#fff',
                      '--bs-btn-active-bg': 'var(--hc-accent-teal)',
                      '--bs-btn-active-border-color': 'var(--hc-accent-teal)',
                    }}
                    onClick={() => onOpenGroup(t.tin)}
                  >
                    <MdGroups size={16} /> {titleCase(t.tin_type)} {t.tin} <MdChevronRight size={16} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="col-12 col-sm-6">
        <MetricCard label="Codes Paid" value={count(data?.summary?.code_count)} icon={<MdReceiptLong size={22} />} loading={loading} />
      </div>
      <div className="col-12 col-sm-6">
        <MetricCard label="Codes With Price Differences" value={count(data?.summary?.varying_code_count)} icon={<MdWarningAmber size={22} />} hint="Same code, different price across networks or settings" loading={loading} />
      </div>

      <div className="col-12">
        <BaseChart
          title="Where this provider is paid differently"
          subtitle="Lowest to highest price for the same code"
          height="440px"
          loading={loading}
          noData={!loading && !varying.length}
          option={feeRangeOption({ data: varying, title: 'Where This Provider Is Paid Differently', subtitle: 'Lowest to highest price for the same code; dot = median' })}
        />
      </div>

      <div className="col-12">
        <div className="hc-surface rounded-4 p-3 shadow-sm">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
            <h6 className="fw-bold hc-text-primary mb-0">Rate card</h6>
            <input className="form-control form-control-sm" style={{ maxWidth: 240, height: 36 }} placeholder="Filter by code…" value={codeSearch} onChange={(e) => setCodeSearch(e.target.value)} />
          </div>
          <DataGrid columns={columns} rows={codes} loading={loading} skeletonRows={8} defaultRowsPerPage={10} stickyHeader getRowId={(row) => `${row.code}-${row.modifier}`} />
        </div>
      </div>
    </>
  );
}

const providerLabel = (p) => `${[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unnamed provider'} · NPI ${p.npi_number.trim()}`;

function FeeProviderTab({ token, insuranceId, isSuperAdmin, prefillNpi, onOpenZip, onOpenGroup }) {
  const { data, loading, error, run } = useApiRequest();
  const providers = useApiRequest();
  const { run: runProviders } = providers;
  const [npi, setNpi] = React.useState('');

  const search = React.useCallback((value) => {
    if (!NPI_PATTERN.test(value)) return toast.error('NPI must be 10 digits.');
    return run((signal) => getFeeScheduleProviderRates(token, insuranceId, value, signal));
  }, [token, insuranceId, run]);

  React.useEffect(() => {
    if (isSuperAdmin) runProviders(() => getAllProviders(token));
  }, [isSuperAdmin, token, runProviders]);

  React.useEffect(() => {
    if (!prefillNpi) return;
    setNpi(String(prefillNpi.npi));
    search(String(prefillNpi.npi));
  }, [prefillNpi, search]);

  // Only providers with a valid NPI can be looked up in the payer's files.
  const providerOptions = React.useMemo(
    () => (Array.isArray(providers.data) ? providers.data : [])
      .filter((p) => NPI_PATTERN.test((p.npi_number || '').trim()))
      .map((p) => ({ value: p.npi_number.trim(), label: providerLabel(p) })),
    [providers.data],
  );

  const choose = (value) => {
    if (!value) return;
    setNpi(value);
    search(value);
  };

  return (
    <div className="row g-4">
      <div className="col-12">
        <form className="hc-surface rounded-4 p-3 shadow-sm row g-3 align-items-end mx-0" onSubmit={(event) => { event.preventDefault(); search(npi); }}>
          {isSuperAdmin ? (
            <div className="col-12 col-md-5">
              <label className="form-label small fw-semibold text-secondary mb-1">Choose from your providers</label>
              <SearchableDropdown
                options={providerOptions}
                value={providerOptions.some((o) => o.value === npi) ? npi : ''}
                onChange={choose}
                placeholder={providers.loading ? 'Loading providers…' : 'Select a provider'}
                emptyMessage="No providers with an NPI found"
              />
            </div>
          ) : null}
          <div className={isSuperAdmin ? 'col-12 col-md-7' : 'col-12'}>
            <label className="form-label small fw-semibold text-secondary mb-1" htmlFor="fee-provider-npi">Provider NPI</label>
            <div className="d-flex align-items-center gap-2 border rounded-2 bg-white ps-3 pe-1" style={{ height: 40 }}>
              <MdSearch size={20} className="text-secondary flex-shrink-0" />
              <input
                id="fee-provider-npi"
                type="text"
                autoComplete="off"
                className="form-control flex-grow-1 border-0 shadow-none bg-transparent px-1"
                inputMode="numeric"
                maxLength={10}
                placeholder="Enter a 10-digit NPI"
                value={npi}
                onChange={(e) => setNpi(e.target.value.replace(/\D/g, ''))}
                style={{ height: 36, minWidth: 0 }}
              />
              <button type="submit" className="btn hc-btn-primary-gradient text-white fw-semibold rounded-2 px-3 px-sm-4 d-inline-flex align-items-center gap-2 flex-shrink-0" style={{ height: 32 }} disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : null}
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {providers.error ? <div className="col-12"><div className="alert alert-danger mb-0">{providers.error.message}</div></div> : null}
      {error ? <div className="col-12"><div className="alert alert-danger mb-0">{error.message}</div></div> : null}
      {!data && !loading && !error ? (
        <div className="col-12">
          <div className="hc-surface rounded-4 p-4 text-center text-secondary">
            Search an NPI to see every rate this payer pays that provider, and where the same code is paid differently across networks.
          </div>
        </div>
      ) : null}

      {data || loading ? <ProviderRateCardResults data={data} loading={loading} onOpenZip={onOpenZip} onOpenGroup={onOpenGroup} /> : null}
    </div>
  );
}

/** The logged-in provider's own rate card, resolved server-side from their login. */
function FeeMyRatesTab({ token, insuranceId, onOpenZip, onOpenGroup }) {
  const { data, loading, error, run } = useApiRequest();

  React.useEffect(() => {
    run((signal) => getFeeScheduleMyRateCard(token, insuranceId, signal));
  }, [token, insuranceId, run]);

  return (
    <div className="row g-4">
      {error ? <div className="col-12"><div className="alert alert-danger mb-0">{error.message}</div></div> : null}
      {data || loading ? <ProviderRateCardResults data={data} loading={loading} onOpenZip={onOpenZip} onOpenGroup={onOpenGroup} /> : null}
    </div>
  );
}


const CODE_COLUMNS = [
  { id: 'code', label: 'Code', minWidth: 90, filterable: true },
  { id: 'modifier', label: 'Modifier', minWidth: 100, align: 'center' },
  { id: 'median', label: 'Median', minWidth: 110, align: 'center', format: (value) => <strong>{usd(value)}</strong> },
  { id: 'min', label: 'Lowest', minWidth: 110, align: 'center', format: (value) => usd(value) },
  { id: 'max', label: 'Highest', minWidth: 110, align: 'center', format: (value) => usd(value) },
  NETWORK_RATES_COLUMN,
];

function FeeGroupTab({ token, insuranceId, prefillTin, onOpenProvider }) {
  const { data, loading, error, run } = useApiRequest();
  const [tin, setTin] = React.useState('');

  const search = React.useCallback((value) => {
    const trimmed = value.trim();
    if (!trimmed) return toast.error('Enter a TIN.');
    return run((signal) => getFeeScheduleGroupRates(token, insuranceId, trimmed, signal));
  }, [token, insuranceId, run]);

  React.useEffect(() => {
    if (!prefillTin) return;
    setTin(prefillTin.tin);
    search(prefillTin.tin);
  }, [prefillTin, search]);

  const specialties = React.useMemo(() => transformFeeNameValues(data?.specialties), [data]);
  const providerColumns = React.useMemo(() => [
    {
      id: 'name',
      label: 'Provider',
      minWidth: 220,
      filterable: true,
      format: (value, row) => (
        <button type="button" className="btn p-0 text-start text-dark border-0 bg-transparent" style={{ font: 'inherit' }} onClick={() => onOpenProvider(row.npi)}>
          {value}
        </button>
      ),
    },
    { id: 'npi', label: 'NPI', minWidth: 120, align: 'center', filterable: true },
    { id: 'specialty', label: 'Specialty', minWidth: 200, format: (value) => value || '—' },
    { id: 'entity_type', label: 'Type', minWidth: 110, align: 'center' },
    { id: 'city', label: 'Location', minWidth: 160, format: (value, row) => [value, row.state].filter(Boolean).join(', ') || '—' },
  ], [onOpenProvider]);

  const shownProviders = data?.providers?.length || 0;

  return (
    <div className="row g-4">
      <div className="col-12">
        <form className="hc-surface rounded-4 p-3 shadow-sm row g-3 align-items-end mx-0" onSubmit={(event) => { event.preventDefault(); search(tin); }}>
          <div className="col-12">
            <label className="form-label small fw-semibold text-secondary mb-1" htmlFor="fee-group-tin">Group TIN</label>
            <div className="d-flex align-items-center gap-2 border rounded-2 bg-white ps-3 pe-1" style={{ height: 40 }}>
              <MdSearch size={20} className="text-secondary flex-shrink-0" />
              <input
                id="fee-group-tin"
                type="text"
                autoComplete="off"
                className="form-control flex-grow-1 border-0 shadow-none bg-transparent px-1"
                maxLength={20}
                placeholder="Enter a tax ID number"
                value={tin}
                onChange={(e) => setTin(e.target.value)}
                style={{ height: 36, minWidth: 0 }}
              />
              <button type="submit" className="btn hc-btn-primary-gradient text-white fw-semibold rounded-2 px-3 px-sm-4 d-inline-flex align-items-center gap-2 flex-shrink-0" style={{ height: 32 }} disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : null}
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {error ? <div className="col-12"><div className="alert alert-danger mb-0">{error.message}</div></div> : null}
      {!data && !loading && !error ? (
        <div className="col-12">
          <div className="hc-surface rounded-4 p-4 text-center text-secondary">
            Search a TIN to see every provider billing under that group and the rates their contracts pay. Open a provider&apos;s rate card to find its TIN.
          </div>
        </div>
      ) : null}

      {data || loading ? (
        <>
          <div className="col-12 col-sm-6 col-xl-6">
            <MetricCard label="Providers in Group" value={count(data?.provider_count)} icon={<MdGroups size={22} />} loading={loading} />
          </div>
          <div className="col-12 col-sm-6 col-xl-6">
            <MetricCard label="Code Rates" value={count(data?.codes?.length)} icon={<MdReceiptLong size={22} />} loading={loading} />
          </div>

          <div className="col-12">
            <BaseChart
              title="Specialties in group"
              subtitle="Providers by primary taxonomy"
              loading={loading}
              noData={!loading && !specialties.length}
              option={feeBarOption({ data: specialties, title: 'Specialties in Group', subtitle: 'Providers by primary taxonomy' })}
            />
          </div>

          <div className="col-12">
            <div className="hc-surface rounded-4 p-3 shadow-sm h-100">
              <h6 className="fw-bold hc-text-primary mb-1">Providers</h6>
              <p className="text-secondary small mb-3">
                {data && data.provider_count > shownProviders ? `Showing ${count(shownProviders)} of ${count(data.provider_count)}` : 'Click a provider to open its rate card'}
              </p>
              <DataGrid columns={providerColumns} rows={data?.providers || []} loading={loading} skeletonRows={6} defaultRowsPerPage={10} getRowId={(row) => row.npi} />
            </div>
          </div>
          <div className="col-12">
            <div className="hc-surface rounded-4 p-3 shadow-sm h-100">
              <h6 className="fw-bold hc-text-primary mb-1">Group rates</h6>
              <p className="text-secondary small mb-3">Rates across every contract shared by this group</p>
              <DataGrid columns={CODE_COLUMNS} rows={data?.codes || []} loading={loading} skeletonRows={6} defaultRowsPerPage={10} getRowId={(row) => `${row.code}-${row.modifier}`} />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

const NOT_FOUND_COLUMNS = [
  { id: 'npi', label: 'NPI', minWidth: 130 },
  { id: 'tin_type', label: 'TIN Type', minWidth: 100, align: 'center', format: (value) => titleCase(value) },
  { id: 'tin', label: 'TIN', minWidth: 130, align: 'center' },
];

function FeeDataHealthTab({ token, insuranceId, networks }) {
  const expiring = useApiRequest();
  const quality = useApiRequest();
  const { run: runExpiring } = expiring;
  const { run: runQuality } = quality;

  React.useEffect(() => {
    runExpiring((signal) => getFeeScheduleExpiringRates(token, insuranceId, signal));
    runQuality((signal) => getFeeScheduleDataQuality(token, insuranceId, signal));
  }, [token, insuranceId, runExpiring, runQuality]);

  const exp = expiring.data || {};
  const dq = quality.data || {};
  const placeholder = dq.placeholder_prices || {};
  const notFound = dq.npi_not_found || {};
  const failed = React.useMemo(() => (networks || []).filter((n) => n.status !== 'completed'), [networks]);
  const expLoading = expiring.loading || (!expiring.data && !expiring.error);
  const dqLoading = quality.loading || (!quality.data && !quality.error);

  const cards = [
    { label: 'Rates With Expiry', value: count(exp.summary?.rate_count), icon: <MdSchedule size={22} />, loading: expLoading },
    { label: 'Already Expired', value: count(exp.summary?.expired_count), icon: <MdEventBusy size={22} />, loading: expLoading },
    { label: `Expiring in ${exp.summary?.expiring_soon_days || 90} Days`, value: count(exp.summary?.expiring_soon_count), icon: <MdHistoryToggleOff size={22} />, loading: expLoading },
    { label: 'Placeholder Prices', value: count(placeholder.rate_count), icon: <MdMoneyOff size={22} />, hint: `≥ $${count(placeholder.threshold || 99999)}`, loading: dqLoading },
    { label: 'NPIs Not in Registry', value: count(notFound.provider_count), icon: <MdPersonOff size={22} />, loading: dqLoading },
    { label: 'Networks Not Completed', value: count(failed.length), icon: <MdErrorOutline size={22} />, loading: false },
  ];
  const amber = theme.gradientEnd();

  return (
    <div className="row g-4">
      {cards.map((card) => (
        <div key={card.label} className="col-12 col-sm-6 col-lg-4 col-xxl-2">
          <MetricCard {...card} />
        </div>
      ))}

      <div className="col-12">
        <BaseChart
          title="Rates by expiration month"
          subtitle="Rates carrying a real expiration date"
          loading={expLoading}
          error={expiring.error}
          noData={!expLoading && !(exp.by_month || []).length}
          option={feeBarOption({ data: exp.by_month || [], title: 'Rates by Expiration Month', subtitle: 'Rates carrying a real expiration date', color: amber })}
        />
      </div>
      <div className="col-12 col-xl-6">
        <BaseChart
          title="Expiring rates by network"
          subtitle="Top networks"
          loading={expLoading}
          error={expiring.error}
          noData={!expLoading && !(exp.by_network || []).length}
          option={feeBarOption({ data: exp.by_network || [], title: 'Expiring Rates by Network', subtitle: 'Top networks', color: amber })}
        />
      </div>
      <div className="col-12 col-xl-6">
        <BaseChart
          title="Expiring rates by code"
          subtitle="Top billing codes"
          loading={expLoading}
          error={expiring.error}
          noData={!expLoading && !(exp.by_code || []).length}
          option={feeBarOption({ data: exp.by_code || [], title: 'Expiring Rates by Code', subtitle: 'Top billing codes', color: amber })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Placeholder prices by code"
          subtitle="Rates published as $99,999"
          loading={dqLoading}
          error={quality.error}
          noData={!dqLoading && !(placeholder.by_code || []).length}
          option={feeBarOption({ data: placeholder.by_code || [], title: 'Placeholder Prices by Code', subtitle: 'Rates published as $99,999 instead of a real price' })}
        />
      </div>
      <div className="col-12 col-xl-6">
        <BaseChart
          title="Placeholder prices by network"
          subtitle="Rates published as $99,999"
          loading={dqLoading}
          error={quality.error}
          noData={!dqLoading && !(placeholder.by_network || []).length}
          option={feeBarOption({ data: placeholder.by_network || [], title: 'Placeholder Prices by Network', subtitle: 'Rates published as $99,999 instead of a real price' })}
        />
      </div>

      <div className="col-12">
        <div className="hc-surface rounded-4 p-3 shadow-sm h-100">
          <h6 className="fw-bold hc-text-primary mb-1">NPIs not in the national registry</h6>
          <p className="text-secondary small mb-3">
            {notFound.provider_count > (notFound.sample || []).length ? `Sample of ${count((notFound.sample || []).length)} of ${count(notFound.provider_count)}` : 'Providers the NPPES lookup could not find'}
          </p>
          <DataGrid columns={NOT_FOUND_COLUMNS} rows={notFound.sample || []} loading={dqLoading} skeletonRows={5} defaultRowsPerPage={10} getRowId={(row) => `${row.npi}-${row.tin}`} />
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'zip', label: 'Zip Lookup', icon: MdLocationOn },
  { id: 'provider', label: 'Provider', icon: MdPerson },
  { id: 'group', label: 'Group (TIN)', icon: MdGroups },
  { id: 'specialty', label: 'Specialty', icon: MdMedicalServices },
];
// Ingestion and data-quality views are for platform operators only.
const SUPER_ADMIN_TABS = [
  { id: 'networks', label: 'Networks', icon: MdHub },
  { id: 'health', label: 'Data Health', icon: MdHealthAndSafety },
];

const CHAT_WELCOME_PROMPTS = [
  'Compare base rates for 99213, 99214 and 99215',
  'Who are the highest paid providers for 99214 in 77030?',
  'Which networks have the most expiring rates?',
];
const CHAT_THINKING_MESSAGES = [
  "Reading the payer's published rates...",
  'Comparing negotiated rates...',
  'Ranking providers by rate...',
  'Checking networks and contracts...',
  'Calculating rate percentiles...',
  'Putting the findings together...',
];

export default function FeeScheduleAnalyticsView() {
  useChartTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { token, decodedToken } = useAuth();
  const overview = useApiRequest();
  const { run: runOverview } = overview;

  const isSuperAdmin = isSuperAdminUser(decodedToken);
  // Provider users open on their own rates; super admins pick providers inside the Provider tab instead.
  const showMyRates = Boolean(decodedToken?.provider_id) && !isSuperAdmin;
  const tabs = React.useMemo(() => [
    ...(showMyRates ? [{ id: 'mine', label: 'My Rates', icon: MdAccountCircle }] : []),
    ...TABS,
    ...(isSuperAdmin ? SUPER_ADMIN_TABS : []),
  ], [showMyRates, isSuperAdmin]);
  const initialTab = showMyRates ? 'mine' : 'zip';

  const routePayer = location?.state?.payer || null;
  const [active, setActive] = React.useState(initialTab);
  // Tabs stay mounted after their first visit so switching back keeps their results.
  const [visited, setVisited] = React.useState(() => new Set([initialTab]));
  const [zipPrefill, setZipPrefill] = React.useState(null);
  const [providerPrefill, setProviderPrefill] = React.useState(null);
  const [groupPrefill, setGroupPrefill] = React.useState(null);

  React.useEffect(() => {
    if (token && id) runOverview((signal) => getFeeScheduleOverview(token, id, signal));
  }, [token, id, runOverview]);

  React.useEffect(() => {
    if (overview.error) toast.error(overview.error.message);
  }, [overview.error]);

  const openTab = React.useCallback((key) => {
    setActive(key);
    setVisited((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);
  // Prefill objects are recreated on every click so the target tab re-runs even for the same value.
  const openZip = React.useCallback((values) => { setZipPrefill({ ...values }); openTab('zip'); }, [openTab]);
  const openProvider = React.useCallback((npi) => { setProviderPrefill({ npi }); openTab('provider'); }, [openTab]);
  const openGroup = React.useCallback((tin) => { setGroupPrefill({ tin }); openTab('group'); }, [openTab]);

  const data = overview.data;
  const payer = data?.insurance || (routePayer ? { name: routePayer.name, code: routePayer.code } : null);
  const summary = data?.summary || {};
  const codes = React.useMemo(() => data?.codes || [], [data]);
  const loading = overview.loading || (!data && !overview.error);

  const tiles = [
    { label: 'Networks', value: count(summary.network_count), icon: <MdHub size={22} color="#f59e0b" /> },
    { label: 'Rates in Files', value: count(summary.rate_count), icon: <MdReceiptLong size={22} color="var(--hc-brand-ink)" /> },
    { label: 'Billing Codes', value: count(summary.code_count), icon: <MdWorkspaces size={22} color="#0d6efd" /> },
    { label: 'Completed Networks', value: count(summary.completed_network_count), icon: <MdBusinessCenter size={22} color="#16a34a" /> },
  ];

  const panel = (key, content) => (visited.has(key) ? <div key={key} className={active === key ? '' : 'd-none'}>{content}</div> : null);

  return (
    <div className="container-fluid py-4">
      <PageTitle title={`${payer?.name || 'Fee Schedule Analytics'} - Aegis Health 360`} />
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        <button
          type="button"
          className="btn btn-light fw-semibold border shadow-sm d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
          onClick={() => navigate(-1)}
          style={{ minHeight: 40 }}
        >
          <MdArrowBack size={18} />
          <span>Back</span>
        </button>
        <AnalyticsChatWidget
          requestContext={{ insurance_id: Number(id) }}
          chatEndpoint={endpoints.FEE_SCHEDULE_CHAT}
          summaryEndpoint={endpoints.FEE_SCHEDULE_CHAT_SUMMARY_EMAIL}
          permissionCode={PermissionCodes.FEE_SCHEDULE_ANALYTICS_VIEW}
          title="Fee Schedule Assistant"
          subtitle={payer?.name}
          triggerLabel="Ask Fee Schedule AI"
          emptyTitle="Ask me about this payer's rates"
          emptyDescription={`Rates by code, zip code, provider, group and network — for ${payer?.name || 'this payer'}.`}
          welcomePrompts={CHAT_WELCOME_PROMPTS}
          thinkingMessages={CHAT_THINKING_MESSAGES}
          placeholder="Ask about rates, codes, providers, zip codes..."
        />
      </div>

      <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
        <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
        <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
        <div className="position-relative">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="px-3 py-2 rounded-pill fw-semibold bg-success text-white" style={{ fontSize: '0.82rem' }}>
              Fee Schedule Analytics
            </span>
            {payer?.code ? (
              <span className="px-3 py-2 rounded-pill fw-semibold bg-info text-white" style={{ fontSize: '0.75rem' }}>{payer.code}</span>
            ) : null}
          </div>
          <h2 className="fw-bold text-white mb-3" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>{payer?.name || 'Payer'}</h2>
          <div className="row g-3">
            {tiles.map((tile) => (
              <div key={tile.label} className="col-12 col-sm-6 col-xl-3">
                {loading ? (
                  <div className="skeleton-hero position-relative overflow-hidden rounded-4" style={{ height: 76 }} />
                ) : (
                  <HeroStatTile icon={tile.icon} label={tile.label} value={tile.value} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="hc-dash-segwrap">
          <SectionBar tabs={tabs} activeId={active} onChange={openTab} />
        </div>
      </div>

      <div className="p-4 p-lg-5 hc-surface hc-card-primary">
        {panel('zip', <FeeZipTab codes={codes} token={token} insuranceId={id} prefill={zipPrefill} onOpenProvider={openProvider} />)}
        {showMyRates ? panel('mine', <FeeMyRatesTab token={token} insuranceId={id} onOpenZip={openZip} onOpenGroup={openGroup} />) : null}
        {panel('provider', <FeeProviderTab token={token} insuranceId={id} isSuperAdmin={isSuperAdmin} prefillNpi={providerPrefill} onOpenZip={openZip} onOpenGroup={openGroup} />)}
        {panel('group', <FeeGroupTab token={token} insuranceId={id} prefillTin={groupPrefill} onOpenProvider={openProvider} />)}
        {panel('specialty', <FeeSpecialtyTab codes={codes} token={token} insuranceId={id} onOpenProvider={openProvider} />)}
        {isSuperAdmin ? panel('networks', <FeeNetworksTab overview={data} loading={loading} token={token} insuranceId={id} />) : null}
        {isSuperAdmin ? panel('health', <FeeDataHealthTab token={token} insuranceId={id} networks={data?.networks} />) : null}
      </div>
    </div>
  );
}
